/**
 * AnswerForm -- Inline form for answering a checklist question
 *
 * Renders the appropriate input control based on question type:
 * - yes_no: Segmented toggle with compliance labels and N/A state
 * - date: DatePicker (compound, from UI library)
 * - text: Textarea (auto-resize)
 * - document: Input with placeholder
 * - selection: Textarea fallback (options TBD)
 *
 * Shows hint + expectedEvidence in a collapsible details section.
 * Footer has Cancel and Save Answer buttons.
 */

import { useState, useCallback } from "react";
import * as Popover from "@radix-ui/react-popover";
import type { GeneratedQuestion } from "@/packages/types";
import { Button, Input, Textarea, DatePicker } from "@/components/ui";
import { useTranslation } from "@/packages/utils";

// ─── Props ──────────────────────────────────────────────────────────────────

interface AnswerFormProps {
  question: GeneratedQuestion;
  initialValue?: string | boolean | number;
  initialNotes?: string;
  onSave: (value: string | boolean | number, notes?: string) => void;
  onCancel: () => void;
  isSaving: boolean;
}

// ─── Default Values ─────────────────────────────────────────────────────────

function getDefaultValue(
  type: GeneratedQuestion["type"],
): string | boolean | number {
  switch (type) {
    case "yes_no":
      return false;
    case "date":
      return Date.now();
    case "text":
    case "document":
    case "selection":
      return "";
  }
}

// ─── Yes/No Toggle ──────────────────────────────────────────────────────────

function YesNoInput({
  value,
  onChange,
  yesLabel,
  noLabel,
  naLabel,
}: {
  value: boolean | "not_applicable";
  onChange: (v: boolean | "not_applicable") => void;
  yesLabel: string;
  noLabel: string;
  naLabel: string;
}) {
  return (
    <div className="flex items-center gap-0.5 rounded-md bg-muted/20 p-0.5 w-fit">
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`rounded px-3 py-1 text-[12px] font-medium transition-colors ${
          value === true
            ? "bg-green-500 text-white shadow-sm"
            : "text-secondary hover:text-primary"
        }`}
      >
        {yesLabel}
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`rounded px-3 py-1 text-[12px] font-medium transition-colors ${
          value === false
            ? "bg-red-500 text-white shadow-sm"
            : "text-secondary hover:text-primary"
        }`}
      >
        {noLabel}
      </button>
      <button
        type="button"
        onClick={() => onChange("not_applicable")}
        className={`rounded px-3 py-1 text-[12px] font-medium transition-colors ${
          value === "not_applicable"
            ? "bg-muted text-primary shadow-sm"
            : "text-secondary hover:text-primary"
        }`}
      >
        {naLabel}
      </button>
    </div>
  );
}

// ─── Date Input ─────────────────────────────────────────────────────────────

function DateInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  const dateValue =
    value && !Number.isNaN(value) ? new Date(value) : new Date();

  const handleChange = useCallback(
    (date: Date | undefined) => {
      if (date) {
        onChange(date.getTime());
      }
    },
    [onChange],
  );

  return (
    <DatePicker value={dateValue} onChange={handleChange} autoConfirm>
      <DatePicker.Trigger variant="input" />
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          className="z-50 rounded-[12px] border border-border bg-card shadow-lg"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DatePicker.Calendar />
        </Popover.Content>
      </Popover.Portal>
    </DatePicker>
  );
}

// ─── Hint Section ───────────────────────────────────────────────────────────

function HintSection({
  question,
  hintLabel,
  expectedEvidenceLabel,
}: {
  question: GeneratedQuestion;
  hintLabel: string;
  expectedEvidenceLabel: string;
}) {
  if (!question.hint && !question.expectedEvidence) {
    return null;
  }

  return (
    <details className="mt-2">
      <summary className="text-[11px] text-muted cursor-pointer select-none">
        {hintLabel}
      </summary>
      {question.hint && (
        <p className="text-[12px] text-secondary mt-1">{question.hint}</p>
      )}
      {question.expectedEvidence && (
        <p className="text-[11px] text-muted mt-0.5">
          {expectedEvidenceLabel} {question.expectedEvidence}
        </p>
      )}
    </details>
  );
}

// ─── Main Export ─────────────────────────────────────────────────────────────

export function AnswerForm({
  question,
  initialValue,
  initialNotes,
  onSave,
  onCancel,
  isSaving,
}: AnswerFormProps) {
  const { t } = useTranslation(["audits", "common"]);
  const [value, setValue] = useState<string | boolean | number>(
    initialValue ?? getDefaultValue(question.type),
  );
  const [notes, setNotes] = useState(initialNotes ?? "");

  return (
    <div className="rounded-lg border border-accent/20 bg-accent/5 px-3.5 py-3 space-y-3">
      {/* Input control per type */}
      {question.type === "yes_no" && (
        <YesNoInput
          value={
            value === "not_applicable"
              ? "not_applicable"
              : value === true || value === "true"
          }
          onChange={(v) => setValue(v)}
          yesLabel={t("checklist.questions.yesCompliant")}
          noLabel={t("checklist.questions.noNonCompliant")}
          naLabel={t("common:actions.na", "N/A")}
        />
      )}

      {question.type === "date" && (
        <DateInput
          value={
            typeof value === "number" ? value : Number(value) || Date.now()
          }
          onChange={(v) => setValue(v)}
        />
      )}

      {question.type === "text" && (
        <Textarea
          value={String(value)}
          onChange={(e) => setValue(e.target.value)}
          placeholder={t("checklist.answerForm.textPlaceholder")}
          className="min-h-[80px]"
        />
      )}

      {question.type === "document" && (
        <Input
          value={String(value)}
          onChange={(e) => setValue(e.target.value)}
          placeholder={t("checklist.answerForm.documentPlaceholder")}
        />
      )}

      {question.type === "selection" && (
        <Textarea
          value={String(value)}
          onChange={(e) => setValue(e.target.value)}
          placeholder={t("checklist.answerForm.selectionPlaceholder")}
          className="min-h-[80px]"
        />
      )}

      {/* Hint / expected evidence */}
      <HintSection
        question={question}
        hintLabel={t("checklist.answerForm.hint")}
        expectedEvidenceLabel={t("checklist.answerForm.expectedEvidence")}
      />

      {/* Notes field */}
      <div className="mt-3">
        <label
          htmlFor="answer-notes"
          className="text-[11px] font-medium text-muted uppercase tracking-wide mb-1 block"
        >
          {t("checklist.answerForm.notesOptional")}
        </label>
        <Textarea
          id="answer-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t("checklist.answerForm.notesPlaceholder")}
          className="min-h-[60px] text-sm"
        />
      </div>

      {/* Footer buttons */}
      <div className="flex justify-end gap-2 mt-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={isSaving}
        >
          {t("common:actions.cancel")}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => onSave(value, notes || undefined)}
          disabled={isSaving}
          loading={isSaving}
        >
          {t("checklist.answerForm.saveAnswer")}
        </Button>
      </div>
    </div>
  );
}
